// TelcoTrack - Object Management Interfaces
// Interfaces for network objects (components, výkopy, etc.).
// Runtime shape: `BaseObjectEntity` (object-service); column mapping in `scripts/init-database.sql`.

import type { BudovaMilestonesFlags } from '../constants/object-workflow/budova-milestone-engine';

// Object type constants - Updated 2026
export const OBJECT_TYPES = [
  // POINT types
  'ROZVADEC',
  'BUDOVA',
  'SPOJKOVISTE',
  'JAMA',
  'INFO',
  'KONCOVYBOD',
  // LINE types
  'VYKOP',
  'STAVAJICI',
  // Special
  'UNASSIGNED' // Objects from unmapped GeoJSON layers (not shown on map)
] as const;

// Polygon status constants (Czech labels). Objects use workflow phases from object-workflow.constants.ts.
export const STATUS_RED = 'Nezačato';
export const STATUS_YELLOW = 'Probíhá';
export const STATUS_GREEN = 'Hotové';

// DISTRIBUTION_POINT_TYPES, SERVICE_TYPES, SERVICE_LEVELS removed - no longer used (CABLE, DP, CS removed)

export type ObjectType = typeof OBJECT_TYPES[number];

// Czech status definitions (polygon / information completeness display)
export const CZECH_STATUS = {
  NO_INFORMATION: {
    code: 'NO_INFORMATION',
    czechName: 'Červená',
    czechDescription: 'Žádné informace', // No information uploaded
    englishDescription: 'No information uploaded',
    color: '#dc2626',
    meaning: 'information_completeness'
  },
  IN_PROGRESS: {
    code: 'IN_PROGRESS', 
    czechName: 'Žlutá',
    czechDescription: 'Částečné informace', // Partial information, work in progress
    englishDescription: 'Partial information, work in progress',
    color: '#eab308',
    meaning: 'information_completeness'
  },
  COMPLETE: {
    code: 'COMPLETE',
    czechName: 'Zelená', 
    czechDescription: 'Kompletní dokumentace', // Complete documentation
    englishDescription: 'Complete documentation',
    color: '#16a34a',
    meaning: 'information_completeness'
  }
} as const;

export interface GeoLocation {
  type: 'Point' | 'LineString' | 'Polygon' | 'MultiLineString' | 'MultiPolygon';
  coordinates: number[] | number[][] | number[][][] | number[][][][];
  accuracy?: number;
  altitude?: number;
  heading?: number;
  speed?: number;
}

export interface ObjectMetadata {
  tags: string[];
  notes?: string;
  /** Photo IDs for quick reference to `photos` table (binary assets are not stored in metadata JSON). */
  photoIds?: string[];
  lastPhotoUpload?: string; // ISO timestamp of last photo upload
  customFields?: Record<string, any>;
  // Czech-specific fields (kiaCode/ropid live on `objects` columns — do not duplicate in metadata JSON)
  suzDocument?: string; // SUZ document reference
  // Component-specific properties
  componentProperties?: ComponentProperties;
  // Excavation metadata (VYKOP – filled when transitioning to Vykopaný)
  excavationMaterial?: string;
  excavationMaterialSubtype?: string;
  /**
   * Explicitní příjemci nových řádků výkazu (modal Spolupráce). Aktor je v seznamu jen když zůstane zaškrtnutý.
   * Prázdné / chybějící pole = celý řádek připadne tomu, kdo fázi uložil.
   */
  workShareCollaboratorEmails?: string[];
  /**
   * BUDOVA — větve workflow 8/9/10: počet zafouknutých sekundárních OK před přechodem na Zafouknutý (pole API `pripojovanyZafukSekundarnichOkPocet`).
   */
  pripojovanyZafukSekundarnichOkPocet?: number;
  /**
   * BUDOVA milestone workflow — paralelní milníky (zavrtany, zrizeneVr, odboceno, …).
   * Zrcadlí se i do `customFields.budovaMilestones` při zápisu z commitu.
   */
  budovaMilestones?: BudovaMilestonesFlags;
  /** Denormalizovaný seznam ID segmentů odbočkového VYKOPu. */
  branchVykopSegmentIds?: string[];
  /** SPOJKOVISTE — barvy napojené MT (multi-select). */
  napojeneMtBarvy?: string[];
  /** @deprecated Legacy single — první barva z `napojeneMtBarvy`. */
  napojeneMtBarva?: string;
  // Metadata je JSONB bag — povolení neznámých klíčů činí typ přiřaditelný k Record<string, unknown>
  // (shared/web budova-helpery k němu přistupují bracketem). Deklarované klíče si drží svůj typ.
  [key: string]: unknown;
}

// Component properties for different object types (ROZVADEC)
export interface ComponentProperties {
  rozvadecType?: string; // ROZVADEC: see ROZVADEC_TYPES in component.constants
}

